import { DialogContent, DialogTitle, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, ChevronDown, Info, Trash2, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { ResidueEntry } from "@/types/residue";
import { useResidueForm } from "../../hooks/useResidueForm";

interface ResidueModalFormProps {
  onClose: () => void;
  onSave: () => void;
  formEntry: ResidueEntry;
  onFormEntryChange: (entry: ResidueEntry) => void;
  residueOptions: { value: string; label: string }[];
  treatmentOptions: { value: string; label: string; fe_r?: number }[];
  editing: boolean;
}

export const ResidueModalForm = ({
  onClose,
  onSave,
  formEntry,
  onFormEntryChange,
  residueOptions,
  treatmentOptions,
  editing,
}: ResidueModalFormProps) => {
  const { t } = useLanguage();
  const {
    dropdownRef,
    search,
    showDropdown,
    errors,
    isValid,
    filteredOptions,
    setIsScrolling,
    handleTypeChange,
    handleInputClick,
    handleTypeBlur,
    handleTypeSelect,
    handleTreatmentToggle,
    handleQuantityChange,
    handleQuantityBlur,
    handleSaveClick,
  } = useResidueForm({ formEntry, residueOptions, editing, onFormEntryChange, onSave });

  return (
    <DialogContent className="sm:max-w-[550px] h-[85vh] max-h-[720px] flex flex-col">
      <DialogHeader>
        <DialogTitle className="text-xl">
          {editing ? t("footprints.residue.editEntry") : t("footprints.residue.addEntry")}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-6 py-4 overflow-y-auto flex-grow">
        {/* Type Selection */}
        <div className="space-y-2">
          <Label htmlFor="residue-type" className="text-sm font-medium text-gray-700">
            {t("footprints.residue.type")} *
          </Label>
          <div className="relative" ref={dropdownRef}>
            <div className="relative">
              <input
                id="residue-type"
                type="text"
                value={search}
                onChange={handleTypeChange}
                onBlur={handleTypeBlur}
                onClick={handleInputClick}
                placeholder={t("footprints.residue.selectType")}
                autoComplete="off"
                spellCheck="false"
                className="w-full h-11 px-3 py-2 cursor-pointer pr-10 border border-gray-200 rounded-md outline-none focus:outline-none"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                {search && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTypeSelect({ value: "", label: "" }, e);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded-full mr-1"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                <ChevronDown
                  className={`h-4 w-4 text-gray-500 transition-transform ${showDropdown ? "transform rotate-180" : ""}`}
                />
              </div>
            </div>
            {showDropdown && filteredOptions.length > 0 && (
              <div
                className="absolute z-50 w-full mt-1 border bg-white rounded-md shadow-lg overflow-hidden"
                style={{ maxHeight: "320px", overflowY: "auto" }}
                onMouseDown={(e) => {
                  // Prevent dropdown from closing when clicking on scrollbar
                  const target = e.currentTarget;
                  const rect = target.getBoundingClientRect();
                  const isScrollbarClick = e.clientX > rect.right - 16;
                  
                  if (isScrollbarClick) {
                    e.stopPropagation();
                    e.preventDefault();
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onPointerDown={(e) => {
                  e.stopPropagation();
                }}
                onScroll={() => {
                  // Set scrolling state when user scrolls
                  setIsScrolling(true);
                  // Reset after scrolling stops
                  setTimeout(() => setIsScrolling(false), 150);
                }}
              >
                {filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className="w-full text-left px-4 py-3.5 hover:bg-teal-50 cursor-pointer transition-colors border-b border-gray-100 last:border-0"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleTypeSelect(option, e);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {errors.type && (
            <div className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {errors.type}
            </div>
          )}
        </div>

        {/* Treatment Types */}
        <div className="space-y-3 pt-2 mt-6">
          <Label className={`text-sm font-medium ${errors.treatments ? "text-red-500" : "text-gray-700"}`}>
            {" "}
            {t("footprints.residue.treatmentType")} *
          </Label>

          {!formEntry.residueType ? (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 flex items-center gap-3 text-gray-600">
              {" "}
              <Info className="h-5 w-5 text-teal-600 flex-shrink-0" />
              <p className="text-sm">{t("footprints.residue.selectTypeFirst")}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {treatmentOptions.length === 0 ? (
                <div className="text-gray-500 text-sm italic text-center py-6">
                  {" "}
                  {t("footprints.residue.noTreatmentsAvailable")}
                </div>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2 p-4 bg-gray-50 border-b border-gray-200">
                    {treatmentOptions.map((option) => {
                      const isSelected = formEntry.treatments.some((t) => t.treatmentTypeId === option.value);
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => handleTreatmentToggle(option.value, !isSelected)}
                          className={`px-4 py-1.5 rounded-full text-sm transition-all duration-200 focus:outline-none ${
                            isSelected
                              ? "bg-teal-600 text-white hover:bg-teal-700"
                              : "bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  {formEntry.treatments.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {formEntry.treatments.map((treatment) => {
                        const option = treatmentOptions.find((opt) => opt.value === treatment.treatmentTypeId);
                        const quantityError = errors.quantities[treatment.treatmentTypeId];
                        return (
                          <div key={treatment.treatmentTypeId} className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div className="h-2 w-2 bg-teal-600 rounded-full mr-2" />
                                <span className="text-md font-medium text-gray-800">{option?.label}</span>
                              </div>
                              <div className="flex items-center gap-2 w-full max-w-[250px]">
                                <div className="relative w-full">
                                  <Input
                                    id={`quantity-${treatment.treatmentTypeId}`}
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={treatment.quantity}
                                    onChange={(e) =>
                                      handleQuantityChange(
                                        treatment.treatmentTypeId,
                                        e.target.value === "" ? "" : parseFloat(e.target.value)
                                      )
                                    }
                                    onBlur={() => handleQuantityBlur(treatment.treatmentTypeId)}
                                    placeholder="0.00"
                                    autoComplete="off"
                                    className={`pr-12 py-3 w-full rounded-md text-lg ${
                                      quantityError
                                        ? "border-red-500 focus:ring-red-500"
                                        : "border-gray-200 focus:ring-teal-500 focus:border-teal-500"
                                    }`}
                                  />
                                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                    <span className="text-gray-500">t</span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleTreatmentToggle(treatment.treatmentTypeId, false)}
                                  className="text-gray-400 hover:text-red-500 transition-colors p-1.5 rounded-full"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            {quantityError && (
                              <div className="text-red-500 text-sm flex items-center gap-1 mt-1 ml-4">
                                <AlertCircle className="h-3.5 w-3.5" />
                                {quantityError}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="py-6 px-4 text-center text-gray-500 text-sm italic">
                      Selecione pelo menos um tipo de tratamento acima
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {errors.treatments && (
            <div className="text-red-500 text-sm mt-1 flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {errors.treatments}
            </div>
          )}
        </div>

        {formEntry.residueType && formEntry.treatments.length > 0 && (
          <div className="mt-6 text-sm text-gray-600 flex items-start gap-2 bg-blue-50 p-4 rounded-md border border-blue-100">
            <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>{t("footprints.residue.footprintNote")}</div>
          </div>
        )}
      </div>

      <DialogFooter className="pt-2 pb-3 px-6 border-t border-gray-100">
        <div className="flex justify-end gap-3 w-full">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleSaveClick}
            disabled={!isValid}
            className="bg-teal-600 hover:bg-teal-700 text-white px-6"
          >
            {t("common.save")}
          </Button>
        </div>
      </DialogFooter>
    </DialogContent>
  );
};
